import { useEffect } from 'react';

import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';

import { getPackageAgreementOptions } from '@drivingdoc/api-client/generated/react-query';
import { PublicPackage } from '@drivingdoc/api-client/generated/types';

import { AgreementContainer } from 'components/Editor/ContentContainers';
import CheckBoxInput from 'components/FormInputs/CheckBoxInput';
import DatePickerInput from 'components/FormInputs/DatePickerInput';
import TextInput from 'components/FormInputs/TextInput';
import { SafeHTML } from 'components/SafeHTML/SafeHTML';
import ParagraphSkeleton from 'components/Skeletons/ParagraphSkeleton';

import { RegistrationFormOutputValues } from '../utils/validationSchema';

interface AgreementStepProps {
  package: PublicPackage;
}

export function AgreementStep(props: AgreementStepProps) {
  const { setValue } = useFormContext<RegistrationFormOutputValues>();
  const packageAgreement = useQuery({
    ...getPackageAgreementOptions({
      path: {
        id: props.package.id,
      },
    }),
    enabled: !!props.package.id,
  });

  // update the form with the hash
  useEffect(() => {
    const hash = packageAgreement.data?.hash;
    if (hash) {
      setValue('package_agreement.hash', hash);
    }
  }, [packageAgreement, setValue]);
  return (
    <Stack gap={2}>
      <Typography variant="h5" align="center">
        Please read and agree to the terms and conditions below to proceed with your registration.
      </Typography>

      <AgreementContainer>
        {packageAgreement.isLoading && <ParagraphSkeleton paragraphs={8} />}
        {!packageAgreement.isLoading && <SafeHTML htmlContent={packageAgreement.data?.html ?? ''} />}
      </AgreementContainer>
      <Typography variant="h5" fontWeight="bold">
        By entering my name and clicking &quot;I Agree&quot;, I confirm that I am either 18 or older and agree to the
        Terms of Service, or that I am the parent/guardian providing consent on behalf of a student under 18.
      </Typography>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextInput name="package_agreement.full_name" label="Full Name" autoComplete="off" />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <DatePickerInput name="package_agreement.agreement_date" label="Date" />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <CheckBoxInput name="package_agreement.agreed" label="I have read and agree to the Terms of Service" />
        </Grid>
      </Grid>
    </Stack>
  );
}
